import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { SKILLS } from "@/data";
import { SectionHeading, Reveal } from "@/components/Reveal";

const Bar = ({ label, value, delay }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      delay,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value, delay]);

  const slug = label.toLowerCase().replace(/\s+/g, "-");

  return (
    <div ref={ref} data-testid={`skill-bar-${slug}`}>
      <div className="flex items-baseline justify-between mb-3">
        <span className="font-display text-lg sm:text-xl font-semibold text-[#F3EFE6]">{label}</span>
        <span className="font-mono text-2xl sm:text-3xl font-bold text-[#D9A441]" data-testid={`skill-value-${slug}`}>
          {display}
          <span className="text-sm text-[#9DAEC4]">%</span>
        </span>
      </div>
      <div className="h-3 rounded-full bg-white/10 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={inView ? { width: `${value}%` } : {}}
          transition={{ duration: 1.8, delay, ease: [0.22, 1, 0.36, 1] }}
          className="relative h-full rounded-full bg-gradient-to-r from-[#8A6420] via-[#D9A441] to-[#F0C568] shadow-[0_0_20px_rgba(217,164,65,0.55)]"
        >
          <div className="absolute inset-0 overflow-hidden rounded-full">
            <div className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default function SkillStats() {
  return (
    <section id="expertise" data-testid="skills-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute top-1/4 left-0 w-[24rem] h-[24rem] rounded-full bg-[#D9A441]/10 blur-[130px] pointer-events-none" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading id="expertise" overline="Expertise Meter" title="Skill level, no exaggeration" />
        <Reveal className="space-y-10">
          {SKILLS.map((s, i) => (
            <Bar key={s.label} label={s.label} value={s.value} delay={i * 0.2} />
          ))}
        </Reveal>
        <Reveal delay={0.3}>
          <p className="mt-10 text-sm text-[#9DAEC4] font-mono tracking-wide" data-testid="skills-note">
            Self-rated from 5+ years of shipped client work.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
